import { createContext, ReactNode, useContext, useEffect, useRef } from 'react';
import { useStore } from 'zustand';
import { getModels, getTrim } from '../../../../utils/api';
import CarCalculation from '../utils/car';
import {
  createFleetCalculatorStore,
  FleetCalculatorStore,
} from './fleet-store';
import useSettingsStore from './settings-store';

type FleetCalculatorStoreApi = ReturnType<typeof createFleetCalculatorStore>;

export const StoreContext = createContext<FleetCalculatorStoreApi | null>(null);

export const CarStoreProvider = ({ children }: { children: ReactNode }) => {
  const storeRef = useRef<FleetCalculatorStoreApi>();
  if (!storeRef.current) {
    storeRef.current = createFleetCalculatorStore();
  }

  const settings = useSettingsStore();
  const store = storeRef.current;
  const activeTrimCodes = useStore(store, (state) => state.activeTrimCodes);
  const updateState = useStore(store, (state) => state.updateState);
  const isAnyModalOpen = useStore(
    store,
    (state) =>
      state.isSwitchCarModalOpen ||
      !!state.detailedCarViewData ||
      state.isFilterModalOpen,
  );
  const activeCarPair = useStore(store, (state) => state.activeCarPair);

  const prevTrimCodesRef = useRef<string[]>([]);

  useEffect(() => {
    const getCarModelsAction = async () => {
      let models = JSON.parse(sessionStorage.getItem('kia-fleet-models')) ?? [];
      if (models.length) return updateState({ allCarModels: models });

      try {
        const { data } = await getModels();
        if (data) {
          updateState({ allCarModels: data });
        }
        sessionStorage.setItem('kia-fleet-models', JSON.stringify(data));
        return data;
      } catch (error) {
        console.error('Failed to fetch car models:', error);
      }
    };
    getCarModelsAction();
  }, [updateState]);

  useEffect(() => {
    const getTrimAndCalculateAction = async (code: string, index: number) => {
      try {
        const { data: car } = await getTrim(code);
        if (car) {
          const currentState = storeRef.current.getState();
          const newActiveCarPair = [...currentState.activeCarPair];
          newActiveCarPair[index] = {
            car,
            calc: new CarCalculation(car, settings),
          };
          updateState({ activeCarPair: newActiveCarPair });
        }
      } catch (error) {
        console.error(`Failed to fetch trim data for code ${code}:`, error);
      }
    };

    const prevTrimCodes = prevTrimCodesRef.current;
    if (activeTrimCodes?.[0] && activeTrimCodes[0] !== prevTrimCodes[0]) {
      getTrimAndCalculateAction(activeTrimCodes[0], 0);
    }
    if (activeTrimCodes?.[1] && activeTrimCodes[1] !== prevTrimCodes[1]) {
      getTrimAndCalculateAction(activeTrimCodes[1], 1);
    }
    prevTrimCodesRef.current = activeTrimCodes || [];
  }, [activeTrimCodes, settings, updateState]);

  useEffect(() => {
    if (activeCarPair.length > 0 && activeCarPair.every((c) => c.car)) {
      const newActiveCarPair = activeCarPair.map((carData) => ({
        car: carData.car,
        calc: new CarCalculation(carData.car, settings),
      }));
      updateState({ activeCarPair: newActiveCarPair });
    }
  }, [settings]);

  useEffect(() => {
    if (isAnyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isAnyModalOpen]);

  return (
    <StoreContext.Provider value={storeRef.current}>
      {children}
    </StoreContext.Provider>
  );
};

export const useCarStoreContext = <T,>(
  selector: (store: FleetCalculatorStore) => T,
): T => {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error(
      'useCarStoreContext must be used within a CarStoreProvider',
    );
  }
  return useStore(store, selector);
};
